import {
  autoUpdate,
  type FloatingContext,
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  flip,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTypeahead,
} from '@floating-ui/react'
import { css, cx } from '@linaria/core'
import { type FC, type ReactNode, type Ref, type RefObject, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CheckIcon from '#icons/check-48.svg'
import ChevronIcon from '#icons/chevron-1-down-24.svg'
import InfoFilledIcon from '#icons/info-circle-24.svg'
import InfoIcon from '#icons/info-circle-outline-24.svg'
import RevertIcon from '#icons/reload-24.svg'

import { fragments, style } from '#styles'
import { Icon } from './Icon'

export type SelectEntry<Id extends string | number | undefined = string, Value extends ReactNode = ReactNode, Data = never> = [Data] extends [never]
  ? {
      id: Id
      value: Value
      searchValue?: string
    }
  : {
      id: Id
      value: Value
      searchValue?: string
      data: Data
    }

export interface SelectProps<Id extends string | number | undefined = string, Value extends ReactNode = ReactNode, Data = never> {
  value?: Id
  defaultValue?: Id
  options: readonly SelectEntry<Id, Value, Data>[]

  disabled?: boolean
  /** Do not reserve space for Label and Error messages */
  slim?: boolean
  kind?: 'default' | 'inline-text'

  /** Placeholder string */
  placeholder?: string
  /** Alt tooltip when hovering the select element */
  title?: string

  selectEntryComponent?: CustomSelectEntryComponent<Id, Value, Data>

  /** Class for styling the select box itself */
  className?: string
  /** Class for styling the dropdown individually */
  dropdownClassName?: string

  overlayRef?: Ref<HTMLDivElement>

  /** An item was selected or reverted to the placeholder */
  onChange?: (id: Id | undefined) => void
  /** The select box has been opened or closed */
  onOpenChange?: (open: boolean) => void

  label?: string
  required?: boolean
  reversible?: boolean
  onRevert?: (id: Id | undefined) => void
  error?: string | boolean

  /** Info detail icon */
  withDetails?: boolean
  /** State to indicate if the details element is currently shown */
  detailsOpen?: boolean
  /** Handler when the details button has been clicked */
  onDetailsClicked?: React.MouseEventHandler
}

const matchReferenceWidth = () => {
  return size({
    apply({ rects, elements, availableHeight }) {
      Object.assign(elements.floating.style, {
        minWidth: `${rects.reference.width - 2}px`,
        maxWidth: 'fit-content',
        maxHeight: `${availableHeight - 10}px`,
      })
    },
  })
}

const useSelectContext = <Id extends string | number | undefined = string, Value extends ReactNode = ReactNode, Data = never>(
  value: Id | undefined,
  options: readonly SelectEntry<Id, Value, Data>[],
  disabled: boolean | undefined,
  placeholder: string | undefined,
  onOpenChange: ((open: boolean) => void) | undefined,
  onChange: ((item: Id | undefined) => void) | undefined,
) => {
  const { t } = useTranslation()

  const [activeOption, setActiveOption] = useState<Id | undefined>()
  const [isOpen, setIsOpen] = useState(false)

  const { selectedIndex, activeIndex, typeAheadValues } = useMemo(() => {
    return {
      selectedIndex: options.findIndex((x) => x.id === value),
      activeIndex: activeOption ? options.findIndex((x) => x.id === activeOption) : null,
      typeAheadValues: {
        current: options.map((x) => {
          if (x.searchValue) {
            return x.searchValue
          }
          if (typeof x.value === 'string') {
            return x.value
          }
          return `${x.id}`
        }),
      },
    }
  }, [options, activeOption, value])

  const { refs, context, floatingStyles } = useFloating({
    placement: 'bottom',
    whileElementsMounted: autoUpdate,
    open: !disabled && isOpen,
    onOpenChange: (open) => {
      if (disabled || options.length <= 0) {
        return
      }
      if (open !== isOpen) {
        setIsOpen(open)
        onOpenChange?.(open)
      }
    },
    middleware: [flip(), shift(), matchReferenceWidth()],
  })

  const listRef = useRef<(HTMLElement | null)[]>([])

  const onMatch = (index: number) => (isOpen ? setActiveOption(options[index].id) : onChange?.(options[index]?.id))

  const {
    interactions,
    typeahead: { isTypingRef, typedValue },
  } = useSelectInteractions(
    context,
    onMatch,
    listRef,
    typeAheadValues,
    activeIndex,
    selectedIndex,
    (index) => setActiveOption(index ? options[index]?.id : undefined),
    options.length,
  )
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(interactions)
  const handleSelect = useCallback(
    (index: number) => {
      onChange?.(options[index].id)
      setIsOpen(false)
      setActiveOption(options[index].id)
    },
    [onChange, options],
  )

  const optionText =
    selectedIndex >= 0
      ? options[selectedIndex].value
      : (placeholder?.length ?? 0) > 0
        ? placeholder
        : t('shared:common.select.placeholder', 'Select variant')

  return {
    refs,
    context,
    floatingStyles,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
    handleSelect,
    typedValue,
    isTypingRef,
    isOpen,

    requiresPlaceholder: selectedIndex < 0,
    selection: value,
    optionText,
    listRef,
    activeIndex,
    selectedIndex,
  }
}

const useSelectInteractions = (
  context: FloatingContext,
  onMatch: (index: number) => void,
  listRef: RefObject<(HTMLElement | null)[]>,
  typeAheadValues: { current: string[] },
  activeIndex: number | null,
  selectedIndex: number,
  onNavigate: (activeIndex: number | null) => void,
  optionCount: number,
) => {
  const click = useClick(context, { event: 'mousedown' })
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'listbox' })
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    selectedIndex,
    onNavigate,
    loop: optionCount > 7,
  })

  const isTypingRef = useRef(false)
  const [typedValue, setTypedValue] = useState<string | undefined>()
  const typeahead = useTypeahead(context, {
    listRef: typeAheadValues,
    activeIndex,
    selectedIndex,
    onMatch: onMatch,
    findMatch: (list, value) => {
      if (!context.open) {
        return undefined
      }
      const lowerCasedValue = value.toLowerCase()
      setTypedValue(value)
      return list.find((item) => item?.toLowerCase().includes(lowerCasedValue))
    },
    onTypingChange: (isTyping) => {
      isTypingRef.current = isTyping
      if (!isTyping) {
        setTypedValue(undefined)
      }
    },
  })

  return {
    interactions: [click, dismiss, role, typeahead, listNav],
    typeahead: {
      typedValue,
      isTypingRef,
    },
  }
}

const selectClass = css`
  display: grid;
  grid-template-columns: 1fr min-content;
  align-items: center;
  row-gap: 2px;
  color: ${style.themeColors.textDefault};

  overflow: hidden;

  > .label-row {
    grid-row: 1;
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: subgrid;
    
    > .label {
      display: flex;
      align-items: center;
      grid-row: 1;
      gap: 2px;
      height: 18px;
      overflow: hidden;
      
      > .text {
        ${fragments.textStyle.body.m.bold};  
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
  
      > .required {
        ${fragments.textStyle.body.m.regular};
        color: ${style.themeColors.textImportant};
      }
  
      > .filler {
        flex: 1;
      }
  
      > .details {
        cursor: pointer;
        position: relative;
        display: flex;
        justify-content: flex-end;
        margin: 0 5px 0 2px;
        > .details-selected {
          position: absolute;
          right: 0;
          opacity: 0;
          transition: 0.25s ease opacity;
        }
  
        > .details-default {
          opacity: 1;
          transition: 0.25s ease opacity;
        }
        
        &:hover, &.open {
          > .details-selected {
            opacity: 1;
          }
          > .details-default {
            opacity: 0;
          }
        }
  
      }
    }
  }


  > .select {
    height: 30px;
    padding: 4px 8px 5px 8px;
    border: 1px solid ${style.themeColors.lineInput};
    background-color: ${style.themeColors.backgroundDefault};    
    outline: 2px solid transparent;
    outline-offset: -2px;
    overflow: hidden;
    transition: 0.25s ease outline-color;
    
    display: flex;
    align-items: center;
    gap: 8px;

    &:focus, &:focus-within {
      outline-color: ${style.themeColors.lineFocus};
    }
    > .value {
      display: inline-block;
      align-items: center;
      gap: 4px;
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;


      &.placeholder {
        color: ${style.themeColors.textDeselected};
      }
    }

    > .icon {
      color: ${style.themeColors.textDefault};
      transition: 0.25s ease color, 0.25s ease transform;
      transform: rotateX(0);

      &.open {
        transform: rotateX(180deg);
      }
    }

    &:hover {
      border: 1px solid ${style.themeColors.lineInputHover};

      > .icon {
        color: ${style.themeColors.textImportant};
      }
    }

    &.with-error {
      border-color: ${style.themeColors.textDanger};

      &:focus {
        outline-color: ${style.themeColors.textDanger};
      }
    }

    &.disabled {
      border-color: ${style.themeColors.textDeselected};

      &.with-error {
        border-color: ${style.themeColors.textDanger};  
      }
      
      background-color: ${style.themeColors.backgroundBoxed};

      > .value {
        color: ${style.themeColors.textDeselected};
      }

      > .icon, > .icon:hover {
        color: ${style.themeColors.textDeselected};
      }
      &:focus {
        outline-color: transparent;
      }
    }
  }

  &[data-kind='inline-text'] {
    grid-template-columns: min-content min-content;
    
    > .select {
      height: auto;
      padding: 0;
      background-color: transparent;
      border: none;
      &:hover {
        color: ${style.themeColors.textImportant};
      }
    }
  }
      
  
  > .error {
    grid-row: 3;
    height: 18px;
    color: ${style.themeColors.textDanger};
  }

  > .revert {
    margin: 0 0 0 8.5px;
    grid-column: 2;
    transition: 0.25s ease opacity, 0.25s ease color;
    opacity: 0;
    
    &.reversible {
      &:not(.disabled) {
        cursor: pointer;
      }
      opacity: 1;
    }
    
    &.disabled {
      color: ${style.themeColors.textDeselected};
    }
  }
`

const overlayClass = css`
  z-index: 1;
  outline: none;
  &:focus {
    outline: none;
  }
`
const dropdownClass = css`
  position: relative;
  display: flex;
  background: ${style.themeColors.backgroundPopout};
  box-shadow: 0px 2px 12px 0px rgba(0, 0, 0, 0.25);
  outline: none;

  > div.type-ahead-value {
    position: absolute;
    top: 5px;
    right: 45px;
    padding: 6px 12px;
    background-color: ${style.themeColors.backgroundDefault};
    color: ${style.themeColors.textImportant};
    border: 2px solid ${style.themeColors.lineDefault};
  }

  > ul {
    overflow-y: auto;
    flex: 1;
  }
`
const selectEntryClass = css`
  display: flex;
  gap: 4px;

  padding: 7px 6px 7px 8px;
  outline: none;

  > .value {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex: 1;
  }

  > .icon {
    color: ${style.themeColors.textImportant};
  }
  
  > .icon.hidden {
    visibility: hidden;
  }

  &.selected, &:hover, &:focus {
    background-color: ${style.themeColors.backgroundBoxed}
  }
`

interface SelectEntryProps<Id extends string | number | undefined = string, Value extends ReactNode = ReactNode, Data = never> {
  entry: SelectEntry<Id, Value, Data>
  index: number
  context: ReturnType<typeof useSelectContext>
  children?: ReactNode
}

export type CustomSelectEntryComponent<Id extends string | number | undefined = string, Value extends ReactNode = ReactNode, Data = never> = FC<
  SelectEntryProps<Id, Value, Data>
>

export const SelectEntryRenderer = <Id extends string | number | undefined = string, Value extends ReactNode = ReactNode, Data = never>({
  entry,
  index,
  context,
  children,
}: SelectEntryProps<Id, Value, Data>) => {
  const { listRef, selection, handleSelect, isTypingRef, getItemProps } = context
  const onClick = useCallback(() => handleSelect(index), [handleSelect, index])
  const onKeyDown = useCallback(
    (evt: React.KeyboardEvent<HTMLElement>) => {
      if (evt.key === 'Enter') {
        evt.preventDefault()
        handleSelect(index)
      }
      if (evt.key === ' ' && !isTypingRef.current) {
        evt.preventDefault()
        handleSelect(index)
      }
    },
    [handleSelect, index, isTypingRef],
  )

  const itemProps = useMemo(
    () =>
      getItemProps({
        onClick: onClick,
        onKeyDown: onKeyDown,
      }),
    [getItemProps, onClick, onKeyDown],
  )

  return (
    <li
      ref={(node) => {
        listRef.current[index] = node
      }}
      role="option"
      aria-selected={selection === entry.id}
      className={cx('entry', selectEntryClass, selection === entry.id && 'selected')}
      {...itemProps}
      tabIndex={index + 1}
      data-index={index}
    >
      <span className="value">{entry.value}</span>
      {children}
      <Icon size="s" aria-hidden className={cx('icon', 'check', selection !== entry.id && 'hidden')} iconUrl={CheckIcon} />
    </li>
  )
}

export const Select = <Id extends string | number | undefined = string, Value extends ReactNode = ReactNode, Data = never>({
  value: externalValue,
  defaultValue,
  options,
  disabled: externalDisabled,

  placeholder,
  onOpenChange,
  onDetailsClicked,
  onChange: setExternalValue,

  className,
  dropdownClassName,

  overlayRef,

  slim,
  error,
  label,
  kind = 'default',

  reversible,
  onRevert,
  required,
  withDetails,
  detailsOpen,
  title,
  selectEntryComponent: SelectEntryComponent = SelectEntryRenderer,
}: SelectProps<Id, Value, Data>): ReactNode => {
  const [internalValue, setInternalValue] = useState<Id | undefined>(defaultValue)
  const disabled = externalDisabled || options.length <= 0
  const value = setExternalValue ? externalValue : internalValue
  const onChange = setExternalValue ? setExternalValue : setInternalValue

  const selectContext = useSelectContext(value, options, disabled, placeholder, onOpenChange, onChange)
  const { refs, context, floatingStyles, getReferenceProps, getFloatingProps, typedValue, isOpen, selection, optionText, requiresPlaceholder } =
    selectContext

  const revert = useCallback(() => {
    if (disabled) {
      return
    }
    onRevert?.(defaultValue)
  }, [onRevert, defaultValue, disabled])

  const headerBar = slim ? !!label || !!withDetails : true
  const bottomBar = slim ? !!error : true

  return (
    <div
      className={cx(selectClass, 'select', className, disabled && 'disabled', options.length === 0 && 'is-empty', !selection && 'with-placeholder')}
      title={title}
      data-kind={kind}
    >
      {headerBar && (
        <div className="label-row">
          <div className="label" role="term">
            {label && <span className="text">{label}</span>}
            {label && required && <span className="required">*</span>}
            <div className="filler" />
            {withDetails && (
              <div className={cx('details', detailsOpen && 'open')} onClick={onDetailsClicked}>
                <Icon iconUrl={InfoIcon} size="s" className={cx('icon', 'details-default')} />
                <Icon iconUrl={InfoFilledIcon} size="s" className={cx('icon', 'details-selected')} />
              </div>
            )}
          </div>
          <div />
        </div>
      )}

      <div
        className={cx('select', error && 'with-error', disabled && 'disabled', isOpen && 'open')}
        tabIndex={disabled ? undefined : 0}
        ref={refs.setReference}
        aria-disabled={disabled}
        {...getReferenceProps()}
        role="option"
      >
        <span className={cx('value', requiresPlaceholder && 'placeholder')}>{optionText}</span>
        <Icon iconUrl={ChevronIcon} className={cx('icon', isOpen && 'open')} size="xs" />
      </div>
      {reversible && (
        <Icon
          iconUrl={RevertIcon}
          className={cx('icon', 'revert', disabled && 'disabled', value !== defaultValue && 'reversible')}
          size="s"
          onClick={revert}
        />
      )}
      {bottomBar && <div className="error">{error}</div>}

      {isOpen && (
        <FloatingPortal preserveTabOrder={true}>
          <FloatingOverlay className={cx(overlayClass)} ref={overlayRef}>
            <FloatingFocusManager context={context} modal={false} initialFocus={selectContext.selectedIndex} guards={false}>
              <div className={cx(dropdownClass, dropdownClassName)} ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
                {(typedValue?.trim().length ?? 0) > 0 && (
                  <div className="type-ahead-value" aria-hidden aria-disabled>
                    {typedValue}
                  </div>
                )}
                <ul>
                  {options.map((option, index) => (
                    <SelectEntryComponent key={option.id} entry={option} index={index} context={selectContext} />
                  ))}
                </ul>
              </div>
            </FloatingFocusManager>
          </FloatingOverlay>
        </FloatingPortal>
      )}
    </div>
  )
}
